import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, FileText, Search, Trash2, Copy, Filter } from "lucide-react";

/**
 * Admin CMS — upload and manage reusable files + images.
 *
 * One list, two filters (image / file). Click an asset to copy its URL
 * — the asset lives at `/api/chat/files/{file_id}` so it can be pasted
 * into blog posts, mail templates and announcements as-is.
 */
export default function AdminCmsPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cms", { params: { kind: kind || undefined, q: q || undefined } });
      setAssets(r.data || []);
    } catch { toast.error("Couldn't load assets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (label.trim()) fd.append("label", label.trim());
      fd.append("kind", file.type.startsWith("image/") ? "image" : "file");
      await api.post("/admin/cms/upload", fd);
      toast.success(`Uploaded ${file.name}`);
      setLabel("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const copyUrl = (a) => {
    const abs = (process.env.REACT_APP_BACKEND_URL || "") + a.url;
    try { navigator.clipboard.writeText(abs); toast.success("URL copied"); }
    catch { toast.error("Copy failed — select the URL manually"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Any page using it will break.`)) return;
    try { await api.delete(`/admin/cms/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const fmt = (n) => (!n ? "" : n < 1024 ? `${n}B` : n < 1024 * 1024 ? `${Math.round(n / 1024)}KB` : `${(n / 1024 / 1024).toFixed(1)}MB`);

  return (
    <div data-testid="admin-cms-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN · CMS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Content <em className="italic text-kb-primary">library</em>
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Upload reusable images and documents once, reference them anywhere (blogs, mail templates, announcements). URLs stay stable across deployments.
      </p>

      <div className="mt-8 kb-card p-5 flex flex-wrap items-end gap-3" data-testid="admin-cms-upload">
        <div className="min-w-[220px] flex-1">
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Label (optional)</label>
          <input className="kb-input mt-1" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Hero image · October campaign" data-testid="cms-label-input" />
        </div>
        <label className="kb-btn-primary cursor-pointer inline-flex items-center gap-2" data-testid="cms-upload-btn">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
        </label>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden">
          {[["", "All"], ["image", "Images"], ["file", "Files"]].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setKind(k)}
              className={`px-3 py-1.5 kb-mono text-[10px] uppercase tracking-wider border-r border-slate-200 last:border-r-0 ${kind === k ? "bg-kb-primary text-white" : "text-kb-ink-muted hover:bg-slate-50"}`}
              data-testid={`cms-filter-${k || "all"}`}
            >
              <Filter className="inline w-3 h-3 mr-1" /> {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-kb-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search by name or label…" className="kb-input pl-8 text-sm" data-testid="cms-search" />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {loading && <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>}
        {!loading && assets.length === 0 && <div className="kb-mono text-xs text-kb-ink-muted">No assets yet.</div>}
        {assets.map((a) => (
          <div key={a.id} className="kb-card p-3 group" data-testid={`cms-asset-${a.id}`}>
            {a.kind === "image" ? (
              <div className="aspect-video rounded-sm overflow-hidden border border-slate-200 bg-slate-50">
                <img src={(process.env.REACT_APP_BACKEND_URL || "") + a.url} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video rounded-sm overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center">
                <FileText className="w-10 h-10 text-kb-ink-muted" strokeWidth={1.2} />
              </div>
            )}
            <div className="mt-2 min-w-0">
              <div className="text-sm font-medium text-kb-ink truncate" title={a.name}>{a.name}</div>
              {a.label && <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted truncate">{a.label}</div>}
              <div className="kb-mono text-[9px] text-kb-ink-light mt-0.5">
                {a.kind === "image" ? <ImageIcon className="inline w-3 h-3 mr-1" /> : <FileText className="inline w-3 h-3 mr-1" />}
                {a.mime || "file"} · {fmt(a.size)}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <button onClick={() => copyUrl(a)} className="kb-btn-outline text-[10px] py-1 px-2 inline-flex items-center gap-1" data-testid={`cms-copy-${a.id}`}><Copy className="w-3 h-3" /> Copy URL</button>
              <button onClick={() => remove(a)} className="text-rose-500 hover:text-rose-700 ml-auto" title="Delete" data-testid={`cms-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
